import React, { useState } from 'react';
import { useI18n } from '../core/i18n';
import { tennisAudio } from '../core/audio';
import { UserProfile, saveUserProfile } from '../core/profile';
import confetti from 'canvas-confetti';
import {
  GraduationCap,
  CheckCircle,
  Circle,
  Sparkles,
  BookOpen,
  Award,
  ChevronRight,
  Flame,
} from 'lucide-react';

interface AcademyViewProps {
  profile: UserProfile;
  onUpdateProfile: (p: UserProfile) => void;
}

export const AcademyView: React.FC<AcademyViewProps> = ({ profile, onUpdateProfile }) => {
  const { t } = useI18n();
  const [activeCourseId, setActiveCourseId] = useState<number>(1);
  const [completedDrills, setCompletedDrills] = useState<string[]>(['c1_d1', 'c1_d2']);

  const courses = [
    {
      id: 1,
      level: 'Level 1: 筑基起步',
      title: '四大握拍体系与就位站姿',
      duration: '45分钟',
      badge: '入门必备',
      overview: '网球击球的一切源于握拍。从大陆式发球截击握拍，到东方式、半西方式正手，以及双手反手握拍的精准八角棱面定位。',
      lessons: [
        {
          id: 'c1_d1',
          name: '球拍柄八角棱面（Bevels）与握拍食指基底关节定位',
          desc: '掌握拍柄从1号到8号棱面的定位法，杜绝握拍滑动。'
        },
        {
          id: 'c1_d2',
          name: '大陆式握拍（Continental Grip）发球/截击统一握法',
          desc: '如手握菜刀，食指第三关节扣在第2棱面，释放手腕内旋空间。'
        },
        {
          id: 'c1_d3',
          name: '半西方式正手握拍（Semi-Western）与刷旋',
          desc: '现代ATP主流握拍，食指第三关节位于第4棱面，最适合击打腰部高度来球。'
        }
      ]
    },
    {
      id: 2,
      level: 'Level 2: 动力链进阶',
      title: '下肢蹬地、髋部转体与分腿垫步 (Split Step)',
      duration: '60分钟',
      badge: '步法核心',
      overview: '网球发力80%源于下肢与核心躯干。掌握分腿垫步（Split Step）的精准起跳落地时机与现代开放式（Open Stance）正手。',
      lessons: [
        {
          id: 'c2_d1',
          name: '分腿垫步起跳黄金时间点 (Split Step Timing)',
          desc: '在对手触球瞬间起跳，利用膝盖微屈的肌腱弹性瞬间朝来球方向蹬出。'
        },
        {
          id: 'c2_d2',
          name: '整体转肩蓄力（Unit Turn）与非持拍手导向',
          desc: '看到来球立即侧身转肩90度，左手指向来球，骨盆与胸腔形成紧绷弹性势能。'
        },
        {
          id: 'c2_d3',
          name: '现代ATP拍头滞后鞭打（Racket Lag & Whip）',
          desc: '手臂完全放松，拍头在击球前自然下坠，由髋部转动将球拍如皮鞭般甩出。'
        }
      ]
    },
    {
      id: 3,
      level: 'Level 3: 旋转物理学',
      title: '上旋球重坠弧线与切削穿刺深度',
      duration: '50分钟',
      badge: '球质跃升',
      overview: '利用马格努斯效应（Magnus Effect）控制网球飞行轨迹。提高过网高度的同时保证球以急剧弧线下坠在底线深处。',
      lessons: [
        {
          id: 'c3_d1',
          name: '垂直雨刷随挥（Windshield Wiper Follow-through）',
          desc: '从球后下方向前上方刷球，拍面在触球后像挡风玻璃雨刷在胸前自然擦过。'
        },
        {
          id: 'c3_d2',
          name: '过网安全余量法则 (Net Clearance Margins)',
          desc: '相持球过网高度保持在网带上方3到4英尺，彻底杜绝下网低级失误。'
        },
        {
          id: 'c3_d3',
          name: '反手低平穿刺切削（Knife Slice Backhand）',
          desc: '拍面高位引拍，向前下方像切面包一样滑过球底，制造贴地低弹跳。'
        }
      ]
    },
    {
      id: 4,
      level: 'Level 4: 俱乐部双打',
      title: '双打网前雨刷器封网与I阵型绝杀',
      duration: '60分钟',
      badge: '双打制胜',
      overview: '双打是空间与几何的较量。学会如何像雨刷器一样随球移动封堵空档，以及如何用暗号和I阵型打乱接发球节奏。',
      lessons: [
        {
          id: 'c4_d1',
          name: '网前雨刷器（Windshield Wiper）联动折返',
          desc: '球去哪边网前移动向同侧压进半步，永远堵死对手直接抽击的直线通道。'
        },
        {
          id: 'c4_d2',
          name: '抢网截击（Poach）时机与步伐偷袭',
          desc: '盯住对手击球准备姿势，一旦其低头引拍，斜向前跨步抢断中路击球。'
        },
        {
          id: 'c4_d3',
          name: 'I阵型背身暗号与发球战术配合',
          desc: '网前选手蹲在中线比暗号，发球员发内角T点，网前选手瞬间起立拦截。'
        }
      ]
    }
  ];

  const activeCourse = courses.find((c) => c.id === activeCourseId) || courses[0];

  const toggleDrill = (drillId: string) => {
    const isCompleted = completedDrills.includes(drillId);
    let nextDrills: string[];
    if (isCompleted) {
      nextDrills = completedDrills.filter((id) => id !== drillId);
    } else {
      nextDrills = [...completedDrills, drillId];
      tennisAudio.playHit('topspin');
      confetti({ particleCount: 30, spread: 50 });

      // Reward EXP
      const nextProfile: UserProfile = {
        ...profile,
        exp: profile.exp + 50,
        drillsCompleted: profile.drillsCompleted + 1,
      };
      onUpdateProfile(nextProfile);
      saveUserProfile(nextProfile);
    }
    setCompletedDrills(nextDrills);
  };

  return (
    <div className="w-full max-w-5xl mx-auto p-3 sm:p-5 space-y-6">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-850 to-slate-900 p-5 rounded-3xl border border-slate-800 shadow-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-lime-500/20 to-sky-500/20 border border-lime-500/40 flex items-center justify-center text-lime-400 shadow-glow-ball">
            <GraduationCap className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-black text-white flex items-center gap-2">
              <span>欢乐网球通关特训营</span>
              <span className="text-[10px] bg-lime-500/20 text-lime-300 px-2 py-0.5 rounded-full border border-lime-500/40 font-bold">
                Joy Academy Pro
              </span>
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">
              分阶进阶课程：从握拍步法、现代动力链、旋转物理学到俱乐部实战双打
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2 text-xs bg-slate-950 px-3 py-1.5 rounded-xl border border-slate-800">
          <span className="text-slate-400">已打卡完成:</span>
          <span className="font-bold text-lime-400">{completedDrills.length} 项训练</span>
          <span className="text-amber-400 font-mono font-bold">(+50 EXP / 项)</span>
        </div>
      </div>

      {/* Course List & Content Container */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Course Navigation */}
        <div className="space-y-2">
          {courses.map((course) => {
            const isSelected = activeCourse.id === course.id;
            return (
              <div
                key={course.id}
                onClick={() => {
                  setActiveCourseId(course.id);
                  tennisAudio.playHit('slice');
                }}
                className={`p-3.5 rounded-2xl border cursor-pointer transition-all ${
                  isSelected
                    ? 'bg-slate-850 border-lime-400 shadow-md ring-1 ring-lime-400/40'
                    : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] text-lime-400 font-bold uppercase tracking-wider">
                    {course.level}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 bg-slate-800 rounded text-slate-400">
                    {course.duration}
                  </span>
                </div>
                <div className="text-xs font-black text-white">{course.title}</div>
              </div>
            );
          })}
        </div>

        {/* Right: Active Course Detail & Lessons */}
        <div className="lg:col-span-2 bg-slate-900/80 p-5 rounded-3xl border border-slate-800 shadow-xl space-y-5">
          <div className="border-b border-slate-800 pb-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs bg-lime-500/20 text-lime-300 px-2 py-0.5 rounded-full font-bold border border-lime-500/40">
                {activeCourse.badge}
              </span>
              <span className="text-xs text-slate-400 font-mono">{activeCourse.level}</span>
            </div>
            <h2 className="text-base sm:text-lg font-black text-white">{activeCourse.title}</h2>
            <p className="text-xs text-slate-300 leading-relaxed mt-2">{activeCourse.overview}</p>
          </div>

          {/* Lessons list with check-off buttons */}
          <div className="space-y-3">
            <h3 className="text-xs font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <span>📋 随堂特训实操打卡 (Drill Checklist)</span>
            </h3>

            {activeCourse.lessons.map((lesson) => {
              const isDone = completedDrills.includes(lesson.id);
              return (
                <div
                  key={lesson.id}
                  onClick={() => toggleDrill(lesson.id)}
                  className={`p-3 rounded-xl border flex items-start space-x-3 cursor-pointer transition-all ${
                    isDone
                      ? 'bg-slate-950/80 border-lime-500/40'
                      : 'bg-slate-950 border-slate-850 hover:border-slate-700'
                  }`}
                >
                  <button className="mt-0.5 text-lime-400">
                    {isDone ? <CheckCircle className="w-5 h-5 fill-lime-500 text-slate-950" /> : <Circle className="w-5 h-5 text-slate-600" />}
                  </button>
                  <div className="flex-1">
                    <div className={`text-xs font-bold ${isDone ? 'text-lime-300 line-through' : 'text-white'}`}>
                      {lesson.name}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5 leading-snug">{lesson.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
